'use client'

import React, { useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'next/navigation';
import classNames from 'classnames'
import {
  useLayout,
  useQueryState,
  useResponsive,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import useCleanRouter from '@dustin-web/microsite-components/src/hooks/use-clean-router'
import Button from '@dustin-web/microsite-components/src/button'
import { ProductList, ProductListOptions, getSortBy } from '@dustin-web/product-components/src/product-list'
import { FacetGroup, FilterBox, Filters } from '@dustin-web/product-components/src/filters'
import { ActiveFilters } from '@dustin-web/product-components/src/filters/active-filters'
import { Drawer } from '@dustin-web/microsite-components/src/drawer'
import type {
  GetProductsForManufacturerQuery,
  GetProductsForManufacturerQueryVariables,
  ProductReferenceFragment
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import { GET_PRODUCTS_FOR_MANUFACTURER } from '@dustin-web/microsite-data/src/graphql/product/get-products-for-manufacturer.graphql'
import InfinityScrollButton from '@dustin-web/microsite-components/src/infinity-scroll-button'
import useInfiniteProducts from '@dustin-web/product-components/src/hooks/products/use-infinite-products'
import { QueryStateProvider } from "@dustin-web/microsite-components/src/hooks"
import type { BrandContentSectionData } from './types';

function addTrailingSlash(value: string) {
  return value.endsWith('/') ? value : value + '/';
}

type ManufacturerProductsWrapperProps = {
  content: BrandContentSectionData
  pageData: GetProductsForManufacturerQuery
}

export const ManufacturerProductsWrapper = ({ content, pageData }: ManufacturerProductsWrapperProps) => {
  const { brandName, categoryPath } = content ?? {}
  const searchParams = useSearchParams();

  if (!brandName) {
    return null
  }

  const searchParamsFromContent = new URLSearchParams(categoryPath?.split('?')[1] ?? '')
  const query = Object.fromEntries([...(searchParams?.entries() ?? []), ...searchParamsFromContent.entries()]);
  const initialCategoryPath = categoryPath?.split('?')[0] || ''

  return (
    <QueryStateProvider query={query}>
      <ManufacturerProductList pageData={pageData} brandName={brandName} initialCategoryPath={initialCategoryPath} />
    </QueryStateProvider>
  )
}

type ManufacturerProductListProps = {
  brandName: string
  initialCategoryPath: string
  pageData?: GetProductsForManufacturerQuery
}

export const ManufacturerProductList = ({
  brandName,
  initialCategoryPath,
  pageData = {},
}: ManufacturerProductListProps) => {
  const [showMobileFilter, setShowMobileFilter] = useState(false)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const [isFirstRender, setFirstRender] = useState(true)
  const { translate } = useTranslations()
  const { queryState, setQueryState } = useQueryState()
  const { responsive } = useLayout()
  const { query } = useCleanRouter()
  const { breakpoints } = useResponsive()
  const productListRef = useRef<HTMLDivElement | null>(null)
  const filterRef = useRef<HTMLDivElement | null>(null)

  const {
    page,
    categorypath: category,
    sortby,
    activeFilters = [],
  } = queryState ?? {}

  const categoryPath = addTrailingSlash(category || initialCategoryPath)

  const { data, isValidating } = useGraphql<
    GetProductsForManufacturerQuery,
    GetProductsForManufacturerQueryVariables
  >(GET_PRODUCTS_FOR_MANUFACTURER, {
    variables: {
      manufacturerName: brandName,
      page: isFirstRender ? 1 : page,
      pageSize: isFirstRender ? page * 18 : 18,
      selectedCategoryPath: categoryPath,
      selectedFacets: activeFilters,
      sortBy: getSortBy(sortby || 'sales'),
    },
  })

  useEffect(() => {
    setFirstRender(false)
  }, [])

  const productListData = data ?? pageData

  const {
    productReferences = [],
    productHits = 0,
    facets = []
  } = productListData?.manufacturerProductSearch ?? {}

  const infiniteProducts = useInfiniteProducts(
    productReferences,
    page,
    activeFilters.length
  )

  const allProducts = data ? infiniteProducts : productReferences ?? []
  const showFilterColumn = facets?.length > 0

  useEffect(() => {
    const filters = activeFilters.filter(f => f.name !== 'path')

    setHasActiveFilters(filters.length > 0)
  }, [activeFilters])

  const clearFilters = () => {
    const params = Object.fromEntries(activeFilters.map(f => [f.name, null]))

    setQueryState({
      ...params,
      page: '',
    })
  }

  query?.delete('page')

  return (
    <div id="page-container" className="c-filter u-pt-16" data-gasource="ManufacturerProductList">
      <div className="container u-mb-96">
        <div className="row sm-gutters">
          {showFilterColumn && (
            <Drawer
              visible={showMobileFilter && responsive.isSmallScreen}
              onClose={() => setShowMobileFilter(false)}
              returnOnlyChildren={!responsive.isSmallScreen}
              className="row no-gutters u-px-0 u-py-0">
              <div className="col-12 col-lg-3">
                {facets.length > 0 && (
                  <div ref={filterRef}>
                    <FilterBox
                      title={translate('mobilefilterlabel')}
                      additionalTopContent={
                        <Button
                          className="button-stripped p-sm u-mb-0"
                          text={translate('ResetAllFilters')}
                          onClick={clearFilters}
                          defaultBtn
                          disabled={!hasActiveFilters}></Button>
                      }
                      activeFilters={
                        showMobileFilter ? (
                          <ActiveFilters facetGroups={facets as FacetGroup[]} mobile />
                        ) : null
                      }>
                      <Filters
                        facets={facets as FacetGroup[]}
                      />
                    </FilterBox>

                    {responsive.isSmallScreen && (
                      <div
                        className="u-sticky-bottom u-bg-white u-px-16 u-py-16 d-flex"
                        style={{ zIndex: 10 }}>
                        <Button
                          className="c-btn u-mb-0 u-w-shrink"
                          text={translate('ResetAllFilters')}
                          disabled={!hasActiveFilters}
                          onClick={() => clearFilters()}></Button>
                        <Button
                          className="c-btn c-btn--primary u-flex-1 u-mb-0"
                          text={translate(
                            hasActiveFilters
                              ? 'MobileCloseAndShowProductsFilterLabel'
                              : 'MobileCloseLabel',
                            { count: productHits }
                          )}
                          onClick={() => setShowMobileFilter(false)}></Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Drawer>
          )}
          <div
            id="filter-product-list"
            className={classNames({
              'col-12 col-lg-9': showFilterColumn,
              'col-12': !showFilterColumn,
            })}>
            {showFilterColumn && (
              <>
                <ProductListOptions
                  disablePageSize
                  disableSearch
                  onMobileFilterOverlayOpen={() => setShowMobileFilter(true)}
                />
                <ActiveFilters
                  facetGroups={facets as FacetGroup[]}
                  onClearFilter={clearFilters}
                />
              </>
            )}
            <div ref={productListRef} className="u-mb-16">
              <ProductList
                infinityScroll
                virtualize={false}
                productReferences={allProducts as ProductReferenceFragment[]}
                loading={isValidating}
                digitalDataOrigin='manufacturerPage'
                showSideButtons={breakpoints.max.SM}
              />
            </div>
            {allProducts!.length > 0 && (
              <InfinityScrollButton
                currentlyViewing={allProducts.length}
                maxViewing={productHits}
                loading={isValidating}
                onClick={() =>
                  setQueryState(
                    {
                      page: (page + 1).toString(),
                    },
                    false
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
